import { getQueryObject } from "@pathwright/features/src/modules/utils/urls"
import {
  getStateFromRoute,
  getUrlFromRoute
} from "@pathwright/ui/src/components/link/utils"
import { useMemo, useRef, useSyncExternalStore } from "react"
import { match as RouteMatch, matchPath } from "react-router-dom"

// A page renders in the background (primary) Switch. An overlay renders in the
// foreground Switch on top of whatever page is in the background.
export type RouteKind = "page" | "overlay"

export type RouteConfig = {
  path: string | string[]
  component?: any
  components?: {
    primary?: any
    secondary?: any
    modal?: any
    chakraModal?: any
    cards?: any
  }
  cards?: any[]
  childRoutes?: RouteConfig[]
  getDefaultLaunchedFrom?: (routeState: any) => unknown
  [key: string]: any
}

export type RegisteredRoute = {
  paths: string[]
  exact: boolean
  // Null when the route renders its routes later (e.g. behind a query), so the
  // location can't be classified yet.
  kind: RouteKind | null
  // One route, or every route of a card stack sharing one <Route>.
  routes: RouteConfig[]
  children: RegisteredRoute[]
}

export type RouteClassification = {
  kind: RouteKind
  route: RouteConfig
  match: RouteMatch<Record<string, string>>
}

export type LocationLike = {
  pathname: string
  search?: string
  hash?: string
  state?: any
}

export type Classify = (pathname: string) => RouteClassification | null

export function getRouteKind(route: RouteConfig): RouteKind {
  const components = route.components
  return route.cards?.length ||
    components?.modal ||
    components?.chakraModal ||
    components?.secondary
    ? "overlay"
    : "page"
}

export const getLocationUrl = (location: LocationLike) =>
  `${location.pathname}${location.search || ""}${location.hash || ""}`

const omitBackground = (state: any) => {
  if (!state || typeof state !== "object" || !("background" in state))
    return state
  const { background, ...rest } = state
  return Object.keys(rest).length ? rest : undefined
}

export const toStoredLocation = (location: LocationLike): LocationLike => ({
  pathname: location.pathname,
  search: location.search || "",
  hash: location.hash || "",
  state: omitBackground(location.state)
})

const toBackgroundLocation = (value: unknown): LocationLike | null => {
  const url = getUrlFromRoute(value)
  if (!url) return null
  const { pathname, search, hash } = new URL(url, window.location.origin)
  return { pathname, search, hash, state: omitBackground(getStateFromRoute(value)) }
}

type RouteTable = {
  key: string
  priority: number
  source: unknown[]
  entries: RegisteredRoute[]
}

let tables: RouteTable[] = []
let version = 0
let notifyScheduled = false
const listeners = new Set<() => void>()

function notify() {
  if (notifyScheduled) return
  notifyScheduled = true
  // Tables may register while rendering (a route that builds its routes after
  // a query), so subscribers re-render after that render instead of during it.
  queueMicrotask(() => {
    notifyScheduled = false
    version++
    listeners.forEach((listener) => listener())
  })
}

const isSameSource = (a: unknown[], b: unknown[]) =>
  a.length === b.length && a.every((item, i) => item === b[i])

/**
 * Register a Switch's routes for classifying locations. `priority` mirrors
 * Switch order across tables (lower is checked first).
 */
export function registerRouteTable(
  key: string,
  priority: number,
  source: unknown[],
  entries: RegisteredRoute[]
) {
  const existing = tables.find((table) => table.key === key)
  if (existing && isSameSource(existing.source, source)) return

  tables = [
    ...tables.filter((table) => table.key !== key),
    { key, priority, source, entries }
  ].sort((a, b) => a.priority - b.priority)
  notify()
}

/** Register a path whose routes aren't known until they render. */
export function registerUnresolvedRoute(
  key: string,
  priority: number,
  path: string
) {
  registerRouteTable(key, priority, [path], [
    { paths: [path], exact: false, kind: null, routes: [], children: [] }
  ])
}

export function resetRouteRegistry() {
  tables = []
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const useRouteRegistryVersion = () =>
  useSyncExternalStore(subscribe, () => version)

// undefined: nothing in these routes matched, so the next table is checked.
// null: a route matched (and its Switch stops there) but nothing classifiable.
function classifyEntries(
  entries: RegisteredRoute[],
  pathname: string
): RouteClassification | null | undefined {
  for (const entry of entries) {
    const match = matchPath<Record<string, string>>(pathname, {
      path: entry.paths,
      exact: entry.exact,
      strict: true
    })
    if (!match) continue
    if (entry.kind === null) return null

    const results: RouteClassification[] = []
    if (match.isExact) {
      const route =
        entry.routes.length === 1
          ? entry.routes[0]
          : entry.routes[entry.paths.indexOf(match.path)]
      if (route) results.push({ kind: entry.kind, route, match })
    }
    // Child routes render alongside their parent, not in a Switch.
    for (const child of entry.children) {
      const result = classifyEntries([child], pathname)
      if (result) results.push(result)
    }
    return (
      results.find((result) => result.kind === "overlay") ||
      results.find((result) => result.kind === "page") ||
      null
    )
  }
  return undefined
}

export const classifyLocation: Classify = (pathname) => {
  for (const table of tables) {
    const result = classifyEntries(table.entries, pathname)
    if (result !== undefined) return result
  }
  return null
}

function getDefaultBackground(
  classification: RouteClassification,
  location: LocationLike
): LocationLike | null {
  const { route, match } = classification
  const routeState = {
    ...route,
    location: { ...location, query: getQueryObject(location.search) },
    match,
    params: match.params,
    school: (window as any).school,
    user: (window as any).user
  }

  let value: unknown = null
  try {
    value = route.getDefaultLaunchedFrom?.(routeState)
  } catch (error) {
    console.log("Routing error in getDefaultLaunchedFrom: ", error)
  }

  return toBackgroundLocation(
    value ||
      (window as any).App?.getStore("navigation").request.getHomeLocation()
  )
}

/**
 * The location rendered by the background Switch. A page is its own
 * background. An overlay uses the background stored on its history entry, or
 * its route's default when opened directly. Null when the location can't be
 * classified yet and no background is stored.
 */
export function resolveBackgroundLocation(
  location: LocationLike,
  classify: Classify = classifyLocation
): LocationLike | null {
  const classification = classify(location.pathname)
  if (classification?.kind === "page") return location

  const stored = location.state?.background
  if (stored) return stored
  if (classification?.kind === "overlay")
    return getDefaultBackground(classification, location)
  return null
}

/** State for the next history entry, carrying the background it opens over. */
export function getBackgroundState(
  action: "push" | "replace",
  current: LocationLike,
  nextState: any,
  classify: Classify = classifyLocation
) {
  if (nextState?.background) return nextState

  // A push opens over whatever the current entry shows. A replace stands in
  // for the current entry, so it keeps that entry's background. Pages record
  // one too: a page ignores it, but a replace can turn its entry into an
  // overlay (e.g. a redirect), which then opens over where the user came from.
  const background =
    action === "push"
      ? resolveBackgroundLocation(current, classify)
      : current.state?.background
  if (!background) return nextState

  return { ...nextState, background: toStoredLocation(background) }
}

type StampableHistory = {
  location: LocationLike
  push: (...args: any[]) => void
  replace: (...args: any[]) => void
}

/** Wrap history.push/replace so every entry carries its background location. */
export function installBackgroundHistory(
  history: StampableHistory,
  classify: Classify = classifyLocation
) {
  const wrap =
    (action: "push" | "replace", original: (...args: any[]) => void) =>
    (to: string | LocationLike, state?: any) => {
      const current = history.location
      if (typeof to === "string") {
        return original(
          to,
          getBackgroundState(action, current, state, classify)
        )
      }
      return original({
        ...to,
        state: getBackgroundState(action, current, to.state, classify)
      })
    }

  history.push = wrap("push", history.push.bind(history))
  history.replace = wrap("replace", history.replace.bind(history))
}

/** The background location for `location`, stable while its URL is unchanged. */
export function useBackgroundLocation(location: LocationLike) {
  const registryVersion = useRouteRegistryVersion()
  const resolved = useMemo(
    () => resolveBackgroundLocation(location),
    [location, registryVersion]
  )

  const stableRef = useRef<LocationLike | null>(null)
  if (
    !resolved ||
    !stableRef.current ||
    getLocationUrl(stableRef.current) !== getLocationUrl(resolved)
  ) {
    stableRef.current = resolved
  }
  return stableRef.current
}
